import { useEffect, useState } from 'react';
import { stanNotify } from '@/lib/stanNotify';
import { getAllGoogleCalendars } from '@/lib/api/integrations';
import { useGoogleCalendarMutation } from '../queries/useGoogleCalendarMutation';
import type { GoogleCalendar, GoogleCalendarData } from '@/lib/api/integrations/types';
import type { IntegrationsResponse } from '@/types';

interface UseGoogleCalendarSettingsProps {
    isOpen: boolean;
    userIntegrations: IntegrationsResponse | undefined;
    refetchIntegrations: () => void;
    onClose: () => void;
}

export const useGoogleCalendarSettings = ({
    isOpen,
    userIntegrations,
    refetchIntegrations,
    onClose,
}: UseGoogleCalendarSettingsProps) => {
    const [loading, setLoading] = useState(false);
    const [allCalendars, setAllCalendars] = useState<GoogleCalendar[]>([]);
    const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
    const [primaryCalendarName, setPrimaryCalendarName] = useState<string>('');
    const [hasError, setHasError] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);

    const { mutateAsync: updateCalendars, isPending } = useGoogleCalendarMutation();

    const googleCalendarIntegration = userIntegrations?.['google-calendar'];
    const integrationData = googleCalendarIntegration?.data as GoogleCalendarData | undefined;

    // Load calendar data when modal opens
    useEffect(() => {
        const loadData = async () => {
            if (!isOpen || !integrationData) return;

            try {
                setLoading(true);
                setDataLoaded(false);

                const response = await getAllGoogleCalendars();
                const calendars = response.calendars || [];

                setAllCalendars(calendars);
                setSelectedCalendarIds(integrationData.calendar_ids || []);
                setPrimaryCalendarName(
                    calendars.find(cal => cal.id === integrationData.primary_calendar_id)?.name ||
                        integrationData.primary_calendar_id ||
                        ''
                );

                setDataLoaded(true);
            } catch (error: any) {
                stanNotify({
                    type: 'error',
                    title: 'Calendar Error',
                    text: error?.response?.data?.message ?? 'Failed to load calendars.',
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isOpen, integrationData]);

    const handleSave = async () => {
        if (selectedCalendarIds.length === 0) {
            setHasError(true);
            return;
        }

        await updateCalendars({ calendar_ids: selectedCalendarIds });
        await refetchIntegrations();
        onClose();
    };

    const handleChange = (selectedLabels: string[]) => {
        // Convert labels back to IDs
        const ids = selectedLabels
            .map(label => allCalendars.find(cal => cal.name === label)?.id)
            .filter((id): id is string => !!id);
        setSelectedCalendarIds(ids);
        setHasError(false);
    };

    // Convert IDs to labels for display
    const selectedLabels = selectedCalendarIds
        .map(id => allCalendars.find(cal => cal.id === id)?.name)
        .filter((name): name is string => !!name);

    // Convert calendars to options format
    const options = allCalendars.filter(cal => cal.id && cal.name).map(cal => ({ id: cal.id, label: cal.name }));

    return {
        loading: loading || isPending,
        dataLoaded,
        primaryCalendarName,
        selectedLabels,
        options,
        hasError,
        handleSave,
        handleChange,
    };
};
